import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import find from 'lodash/find'
import React, { useEffect, useMemo } from 'react'
import type { InlineInputContentOptions, InlineInputProps } from '../InlineInput'
import { InlineInput } from '../InlineInput'
import type { SelectFunctionComponents, SelectOption, SelectProps } from './index'
import { SelectCreatable } from './SelectCreatable'
import isObject from 'lodash/isObject'
import isEqual from 'lodash/isEqual'

export type SelectInlineInputProps<T extends SelectProps = any> = InlineInputProps<T> & {
  selectComponent?: SelectFunctionComponents
  selectOptions?: SelectOption[]
  isMulti?: boolean
}

/**
 * This is just a wrapper for common select and turns it into input,
 * This is the base component that can be used by passing the child component information
 */
export function SelectInlineInput<T extends SelectProps = any>({
  parentInputComponent,
  selectComponent,
  selectOptions,
  displayContent,
  name = 'user',
  updateParams,
  inputProps,
  initialValues,
  isMulti = false,
  editContent,
  ...rest
}: SelectInlineInputProps<T>) {
  const SelectComponent = selectComponent ?? SelectCreatable
  const ParentInput = parentInputComponent || InlineInput

  const searchField = inputProps?.labelKey ?? 'value'
  let option: SelectOption | SelectOption[] = null
  const initialFieldValue: any | any[] = get(initialValues, name)

  //the support for multi, we need to find every object items
  if (isMulti) {
    //ordering was messed up, if we just use filter, we need to loop on every item
    //we need to find every object items
    for (const e of initialFieldValue ?? []) {
      const foundItem = find(selectOptions, (item) => {
        //if is a plain string or number, or any primitive type only compare value directly
        if (!isObject(e)) {
          return e === item[searchField]
        }
        return e[searchField] === item[searchField]
      })

      if (foundItem) {
        option = [...((option as SelectOption[]) ?? []), foundItem]
      }
    }
  } else {
    option = find(selectOptions, { [searchField]: initialFieldValue })
  }

  //we need to re-map the initialValues to { value, label  } object
  const foundInitialValue: unknown = useMemo(() => {
    return {
      _id: initialValues?._id,
      [name]: option,
    }
  }, [initialValues, name, option])

  const isFoundOption = !isEmpty(option)

  //when there is no found values, rollback to original value
  if (!isFoundOption) {
    foundInitialValue[name] = initialFieldValue
  }

  const [newInitialValues, setNewInitialValues] = React.useState(foundInitialValue)

  //we need to prevent the re-rerender of ParentInput, if there are no changes in newInitialValues
  //we have a catch to determine if isEqual which performs a deep comparison between two values to determine if they are equivalent.
  useEffect(() => {
    if (!isEqual(foundInitialValue, newInitialValues)) {
      setNewInitialValues(foundInitialValue)
    }
  }, [foundInitialValue, newInitialValues])

  displayContent = displayContent || (!isFoundOption ? get(initialValues, name) : (option: SelectOption) => option?.label)
  editContent =
    editContent ??
    (({ setNewValue, onChange: _onChange, ...editContentProps }: InlineInputContentOptions) => {
      return (
        <SelectComponent
          onCreateOption={(e) => {
            setNewValue({ name: e, value: e, description: e, label: e, __isCreate: true })
          }}
          onServerCreateOption={(e) => {
            setNewValue(e)
          }}
          onChange={(e) => {
            setNewValue(e)
          }}
          {...{
            ...editContentProps,
            autoFocus: true,
            options: selectOptions,
            ...inputProps,
          }}
        />
      )
    })

  return (
    <ParentInput
      {...{
        ...rest,
        name,
        initialValues: newInitialValues,
        displayContent,
        editContent,
        updateParams:
          updateParams ??
          (({ updatedValue, _id }) => {
            return {
              input: {
                _id,
                [name]: updatedValue?.value ?? null,
              },
            }
          }),
      }}
    />
  )
}
